import { Injectable, signal, computed, effect, Injector, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { CounterEvent, TicketQRData, LegacyTicketData, CustomQRTemplate } from '../models/event.model';
import { StorageService } from './storage.service';
import { STORAGE_KEYS } from '../constants/storage.constants';
import { AttendanceService } from './attendance.service';
import { ServerSyncService } from './server-sync.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private readonly STORAGE_KEY = STORAGE_KEYS.EVENTS;
  private serverSyncService = inject(ServerSyncService);

  // NEW: Primary signal-based state
  private readonly eventsSignal = signal<CounterEvent[]>([]);
  private readonly currentEventSignal = signal<CounterEvent | null>(null);
  private readonly initializationCompleteSignal = signal<boolean>(false);

  // NEW: Public signals for components
  public readonly events = this.eventsSignal.asReadonly();
  public readonly currentEvent = this.currentEventSignal.asReadonly();
  public readonly initializationComplete = this.initializationCompleteSignal.asReadonly();

  // NEW: Public method to clear the current event
  public async clearCurrentEvent(): Promise<void> {
    this.currentEventSignal.set(null);
    await this.storage.remove(STORAGE_KEYS.CURRENT_EVENT);
    console.log('[EventService] Current event cleared from storage and signal.');
  }

  // NEW: Computed signals for derived state
  public readonly hasEvents = computed(() => this.events().length > 0);
  public readonly todaysEvents = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    return this.events().filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= today && eventDate < tomorrow;
    });
  });

  // BACKWARD COMPATIBILITY: Keep observables for gradual migration
  private readonly eventsSubject = new BehaviorSubject<CounterEvent[]>([]);
  public readonly events$ = this.eventsSubject.asObservable();
  private currentEventSubject = new BehaviorSubject<CounterEvent | null>(null);
  public readonly currentEvent$ = this.currentEventSubject.asObservable();

  constructor(public storage: StorageService, private attendanceService: AttendanceService, private injector: Injector) {
    // Sync signals with legacy observables during transition
    effect(() => {
      this.eventsSubject.next(this.events());
    });

    effect(() => {
      this.currentEventSubject.next(this.currentEvent());
    });

    // Initialize by loading events and current event
    this.loadEventsFromStorage().then(() => {
      // After events are loaded, load the current event
      this.loadCurrentEvent();
      // Mark initialization as complete
      this.initializationCompleteSignal.set(true);
      console.log('[EventService] ✅ Initialization complete');
    }).catch(error => {
      console.error('[EventService] ❌ Initialization failed:', error);
      // Still mark as complete even if failed, so components don't wait forever
      this.initializationCompleteSignal.set(true);
    });
  }

  private async loadEventsFromStorage() {
    try {
      // Try to load from the main events storage key
      const storedEvents = await this.storage.get(this.STORAGE_KEY);
      let events: CounterEvent[] = [];

      if (storedEvents) {
        const eventData = JSON.parse(storedEvents);
        // Convert ISO strings back to Date objects
        events = eventData.map((e: any) => this.normalizeEventTemplates({
          ...e,
          date: new Date(e.date)
        }));
        console.log('[EventService] Loaded events from main storage:', events.length);
      }

      // CRITICAL: Also check for events stored in localStorage with events_count_ pattern
      // This handles events that were created but not properly saved to main storage
      this.loadEventsFromDirectStorage(events);

      // Also check for joined events from WebSocket connections
      const joinedEvents = await this.loadJoinedEventsFromStorage();
      console.log('[EventService] Found joined events:', joinedEvents.length);

      // Merge events, avoiding duplicates
      const allEvents = [...events];
      joinedEvents.forEach(joinedEvent => {
        if (!allEvents.find(e => e.id === joinedEvent.id)) {
          allEvents.push(joinedEvent);
        }
      });

      // Always update the events list first
      this.eventsSignal.set(allEvents);
      console.log('[EventService] Total events loaded:', allEvents.length);

      // PERSISTENCE PRIORITY: Handle current event restoration
      const currentEventId = await this.storage.get(STORAGE_KEYS.CURRENT_EVENT);

      if (allEvents.length > 0) {
        if (currentEventId) {
          const currentEvent = allEvents.find(e => e.id === currentEventId);

          if (currentEvent) {
            // PERFECT: Restore the exact current event from storage
            console.log('[EventService] ✅ Restoring current event from storage:', currentEvent.name, currentEventId);
            this.currentEventSignal.set(currentEvent);
            await this.attendanceService.switchEvent(currentEventId, currentEvent.countMethod);
          } else {
            // Current event ID exists but event not found in loaded events
            console.log('[EventService] ⚠️  Current event ID not found in loaded events:', currentEventId);
            console.log('[EventService] Available events:', allEvents.map(e => `${e.name} (${e.id})`));

            // PRESERVE the current event ID - don't delete it yet
            // The event might be loaded later or from a different source
            console.log('[EventService] Keeping current event ID for potential future restoration');

            // For now, set the most recent available event as active (but don't save it as current)
            const mostRecentEvent = allEvents.sort((a, b) =>
              new Date(b.date).getTime() - new Date(a.date).getTime()
            )[0];

            console.log('[EventService] Temporarily using most recent event:', mostRecentEvent.name);
            this.currentEventSignal.set(mostRecentEvent);
            await this.attendanceService.switchEvent(mostRecentEvent.id, mostRecentEvent.countMethod);
          }
        } else {
          // No current event stored - first run or app was reset
          console.log('[EventService] No current event ID stored - selecting most recent event');

          const mostRecentEvent = allEvents.sort((a, b) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
          )[0];

          console.log('[EventService] Setting most recent event as current:', mostRecentEvent.name);
          await this.setCurrentEvent(mostRecentEvent.id);
        }
      } else {
        // No events available
        console.log('[EventService] No events found - app will start empty');

        if (currentEventId) {
          console.log('[EventService] Current event ID exists but no events loaded - preserving ID:', currentEventId);
          // PRESERVE the current event ID - events might be loaded later
        }
      }
    } catch (error) {
      console.error('[EventService] Failed to load events from storage:', error);
      // Don't create any automatic events on error either
    }
  }

  private loadEventsFromDirectStorage(events: CounterEvent[]): void {
    try {
      // Check localStorage for events stored with events_count_ pattern
      const countKeys: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('events_count_')) {
          countKeys.push(key);
        }
      }

      console.log('[EventService] Found events_count_ keys:', countKeys.length);

      countKeys.forEach(key => {
        // Extract event ID from key like "events_count_1750250086611"
        const eventId = key.replace('events_count_', '');
        const count = localStorage.getItem(key);

        console.log('[EventService] Found event with count data:', { eventId, count });

        // Check if we already have this event in our events array
        const existingEvent = events.find(e => e.id === eventId);

        if (!existingEvent) {
          // Create a new event from the localStorage data
          const newEvent: CounterEvent = {
            id: eventId,
            name: `Event ${eventId}`, // We'll try to get a better name later
            date: new Date(), // Default to now, we'll try to get better date later
            countMethod: 'manual', // Default assumption
            enableFraudDetection: true // Enable fraud detection by default for new events
          };

          // Try to get better event name from multiple storage patterns
          this.enhanceEventFromStorage(newEvent, eventId);

          events.push(newEvent);
          console.log('[EventService] ✅ Created event from localStorage:', newEvent.name, newEvent.id);
        }
      });

    } catch (error) {
      console.error('[EventService] Error loading events from direct storage:', error);
    }
  }

  private enhanceEventFromStorage(event: CounterEvent, eventId: string): void {
    try {
      // Pattern 1: event_${eventId} (used by websocket signaling)
      const eventKey1 = `event_${eventId}`;
      const eventData1 = localStorage.getItem(eventKey1);

      // Pattern 2: event_${eventId}_details (used by websocket share modal)
      const eventKey2 = `event_${eventId}_details`;
      const eventData2 = localStorage.getItem(eventKey2);

      console.log('[EventService] Looking for event data:', {
        eventId,
        key1: eventKey1,
        hasData1: !!eventData1,
        key2: eventKey2,
        hasData2: !!eventData2
      });

      // Try pattern 1 first
      if (eventData1) {
        try {
          const parsed = JSON.parse(eventData1);
          console.log('[EventService] Pattern 1 data:', parsed);

          if (parsed.name && parsed.name !== eventId) {
            event.name = parsed.name;
            console.log('[EventService] ✅ Found name in pattern 1:', parsed.name);
          }
          if (parsed.joinedAt || parsed.date) {
            event.date = new Date(parsed.joinedAt || parsed.date);
          }
          if (parsed.countMethod) {
            event.countMethod = parsed.countMethod;
          }
        } catch (error) {
          console.warn('[EventService] Error parsing pattern 1 data for', eventId, error);
        }
      }

      // Try pattern 2 (event_${eventId}_details) if pattern 1 didn't provide all info or for new properties like countMethod
      if (eventData2) {
        try {
          const parsed = JSON.parse(eventData2);
          console.log('[EventService] Pattern 2 data:', parsed);

          if (parsed.eventName && parsed.eventName !== event.name) {
            event.name = parsed.eventName;
            console.log('[EventService] ✅ Found name in pattern 2:', parsed.eventName);
          }
          if (parsed.timestamp) {
            const parsedDate = new Date(parsed.timestamp);
            // Only update date if it's more recent or if current date is default (now)
            if (!event.date || parsedDate.getTime() > event.date.getTime()) {
              event.date = parsedDate;
              console.log('[EventService] ✅ Found date in pattern 2:', parsedDate);
            }
          }
          if (parsed.password && !event.password) {
            event.password = parsed.password;
            console.log('[EventService] ✅ Found password in pattern 2');
          }
          if (parsed.countMethod && parsed.countMethod !== event.countMethod) { // NEW: Read countMethod from pattern 2
            event.countMethod = parsed.countMethod; // NEW
            console.log('[EventService] ✅ Found countMethod in pattern 2:', parsed.countMethod); // NEW
          }
        } catch (error) {
          console.warn('[EventService] Error parsing pattern 2 data for', eventId, error);
        }
      }
    } catch (error) {
      console.error('[EventService] Error enhancing event from storage:', error);
    }
  }

  private async loadJoinedEventsFromStorage(): Promise<CounterEvent[]> {
    try {
      const joinedEvents: CounterEvent[] = [];

      // Check all localStorage keys for joined events
      // DO NOT DELETE ANYTHING - just load events
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('event_')) {
          try {
            const rawData = localStorage.getItem(key);
            if (!rawData) {
              console.log(`[EventService] Skipping empty key: ${key}`);
              continue;
            }

            // Check if the data looks like valid JSON before parsing
            if (rawData.length < 2 || (!rawData.startsWith('{') && !rawData.startsWith('['))) {
              console.warn(`[EventService] Skipping non-JSON data for key: ${key}, data: ${rawData.substring(0, 50)}...`);
              continue;
            }

            const eventData = JSON.parse(rawData);
            if (eventData && eventData.type === 'joined') {
              // Check if we already have this event ID to avoid duplicates in memory
              const existingEvent = joinedEvents.find(e => e.id === eventData.id);
              if (existingEvent) {
                console.log('[EventService] Found duplicate event ID in memory, skipping:', eventData.id);
                continue;
              }

              // Convert joined event to CounterEvent format
              const event: CounterEvent = {
                id: eventData.id,
                name: eventData.name || eventData.id,
                date: new Date(eventData.joinedAt || Date.now()),
                countMethod: 'qr', // Default for joined events
                isWebSocketEvent: true, // Mark as WebSocket event
                enableFraudDetection: true // Enable fraud detection by default for new events
              };

              // Enhance with better name if possible
              this.enhanceEventFromStorage(event, eventData.id);

              joinedEvents.push(event);
              console.log('[EventService] Loaded joined event:', event.name, event.id);
            }
          } catch (error) {
            const rawData = localStorage.getItem(key) || '';
            console.warn('[EventService] Failed to parse event data for key:', key, {
              error: error instanceof Error ? error.message : error,
              dataPreview: rawData.substring(0, 100) + (rawData.length > 100 ? '...' : ''),
              dataLength: rawData.length
            });

            // If the data is completely corrupted and causing issues, we could consider removing it
            // But for now, we'll just skip it to preserve user data
            console.log(`[EventService] Preserving potentially corrupted data for manual recovery: ${key}`);
          }
        }
      }

      console.log('[EventService] Loaded joined events (no deletions):', {
        found: joinedEvents.length,
        preservedInStorage: 'all events kept'
      });

      return joinedEvents;
    } catch (error) {
      console.error('[EventService] Failed to load joined events:', error);
      return [];
    }
  }

  private async loadCurrentEvent() {
    try {
      const currentEventId = await this.storage.get(STORAGE_KEYS.CURRENT_EVENT);
      if (currentEventId) {
        const event = this.getEventById(currentEventId);
        if (event) {
          this.currentEventSignal.set(event);
          console.log('[EventService] ✅ Loaded current event:', event.name);

          // Initialize the event count
          await this.attendanceService.switchEvent(event.id, event.countMethod);
        } else {
          console.log('[EventService] ⚠️  Current event ID found but event not loaded:', currentEventId);
          // DO NOT CLEAR - preserve the current event ID for potential later restoration
          console.log('[EventService] Preserving current event ID for potential restoration');
        }
      } else {
        console.log('[EventService] No current event ID found in storage');
      }
    } catch (error) {
      console.error('[EventService] Failed to load current event:', error);
    }
  }

  private async saveEventsToStorage(events: CounterEvent[]) {
    try {
      const normalized = events.map(event => this.normalizeEventTemplates(event));
      await this.storage.set(this.STORAGE_KEY, JSON.stringify(normalized));
      this.eventsSignal.set(normalized);
    } catch (error: any) {
      console.error('[EventService] Failed to save events to storage:', error);
    }
  }

  getEvents(): CounterEvent[] {
    return this.events();
  }

  createEvent(eventData: {
    name: string;
    date: Date;
    countMethod?: 'manual' | 'qr' | 'barcode';
    ticketFormat?: string; // Broaden type to allow any string
    venueCode?: string;
    zoneCode?: string;
    isSharedEvent?: boolean;
    creatorDeviceId?: string;
    id?: string;
  }): CounterEvent {
    const events = this.getEvents();
    const now = Date.now();

    if (eventData.id) {
      const existingById = events.find(event => event.id === eventData.id);
      if (existingById) {
        console.warn('[EventService] Event with explicit ID already exists, returning existing event', {
          existingId: existingById.id,
          name: existingById.name
        });
        return existingById;
      }
    }

    const signature = [
      (eventData.name || '').trim().toLowerCase(),
      new Date(eventData.date).toISOString(),
      eventData.countMethod || 'manual',
      eventData.ticketFormat || '',
      eventData.venueCode || '',
      eventData.zoneCode || ''
    ].join('|');

    const recentDuplicate = events.find(event => {
      const existingTime = Number(event.id);
      if (!Number.isFinite(existingTime)) {
        return false;
      }
      const existingSignature = [
        (event.name || '').trim().toLowerCase(),
        new Date(event.date).toISOString(),
        event.countMethod || 'manual',
        (event as any).ticketFormat || '',
        event.venueCode || '',
        event.zoneCode || ''
      ].join('|');
      return existingSignature === signature && Math.abs(existingTime - now) <= 2500;
    });

    if (recentDuplicate) {
      console.warn('[EventService] Duplicate event creation detected, returning existing event', {
        existingId: recentDuplicate.id,
        name: recentDuplicate.name
      });
      return recentDuplicate;
    }

    const newEvent: CounterEvent = {
      id: eventData.id || now.toString(),
      enableFraudDetection: true, // Enable fraud detection by default for new events
      isSharedEvent: eventData.isSharedEvent ?? false,
      creatorDeviceId: eventData.creatorDeviceId,
      ...eventData
    };

    const updatedEvents = [...events, newEvent];

    // Sort events by date (newest first)
    const sortedEvents = updatedEvents.sort((a: CounterEvent, b: CounterEvent) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });

    // Update the signal to trigger UI updates
    this.eventsSignal.set(sortedEvents);

    // Save to storage
    this.saveEventsToStorage(sortedEvents);

    // Initialize count to 0 for the new event
    this.attendanceService.setInitialCount(newEvent.id);

    return newEvent;
  }

  generateQrEventSignature(params: {
    name: string;
    date: Date;
    ticketFormat?: string;
    venueCode?: string;
    zoneCode?: string;
  }): string {
    return this.buildQrEventSignature(params);
  }

  async findExistingQrEvent(params: {
    name: string;
    date: Date;
    ticketFormat?: string;
    venueCode?: string;
    zoneCode?: string;
  }): Promise<CounterEvent | null> {
    const events = await firstValueFrom(this.events$);
    const targetSignature = this.generateQrEventSignature(params);

    return events.find(event =>
      (event.countMethod || 'manual') === 'qr' &&
      this.generateQrEventSignature({
        name: event.name,
        date: event.date,
        ticketFormat: event.ticketFormat,
        venueCode: event.venueCode,
        zoneCode: event.zoneCode
      }) === targetSignature
    ) || null;
  }

  getCurrentEvent(): CounterEvent | null {
    return this.currentEvent();
  }

  async setCurrentEvent(eventId: string) {
    try {
      const event = this.getEventById(eventId) || null;
      if (event) {
        // Check if we're actually switching to a different event
        const currentEvent = this.currentEventSignal();
        const isEventSwitch = currentEvent && currentEvent.id !== eventId;

        if (isEventSwitch) {
          console.log('[EventService] Event switch detected, refreshing WebRTC mesh connections');
          // Import WebRTC service dynamically to avoid circular dependencies
          const { WebrtcMeshService } = await import('./webrtc-mesh.service');
          const webrtcMeshService = this.injector?.get(WebrtcMeshService);

          if (webrtcMeshService) {
            // Clean up existing mesh connections
            console.log('[EventService] Cleaning up existing WebRTC mesh for event switch');
            webrtcMeshService.cleanupMesh();

            // Small delay to ensure cleanup is complete
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        this.currentEventSignal.set(event);
        await this.storage.set(STORAGE_KEYS.CURRENT_EVENT, eventId);
        console.log('[EventService] Current event set and saved:', event.name);
        await this.attendanceService.switchEvent(event.id, event.countMethod);
        await this.syncEventTemplatesFromServer(event.id);

        // If this was an event switch and we have WebRTC capabilities, refresh mesh connections
        if (isEventSwitch) {
          const { WebrtcMeshService } = await import('./webrtc-mesh.service');
          const webrtcMeshService = this.injector?.get(WebrtcMeshService);

          if (webrtcMeshService) {
            console.log('[EventService] Refreshing WebRTC mesh connections for event switch');
            try {
              // Small delay to ensure any cleanup from above is complete
              await new Promise(resolve => setTimeout(resolve, 200));

              // Now initialize mesh for the new event to ensure fresh connections
              console.log(`[EventService] Initializing fresh mesh connections for event: ${eventId}`);
              await webrtcMeshService.initMesh(eventId, false, event.password || '');

              // Wait for connections to establish
              await new Promise(resolve => setTimeout(resolve, 2000));

              const connectedPeers = webrtcMeshService.getConnectedPeers();
              console.log(`[EventService] Event switch mesh refresh completed. Connected peers: ${connectedPeers.length}`);

              // If this is a QR event and we have peers, request fresh attendance sync
              if (event && event.countMethod === 'qr' && connectedPeers.length > 0) {
                console.log('[EventService] Requesting fresh attendance sync after event switch');
                // Import WebSocket signaling service to get client ID
                const { WebSocketSignalingService } = await import('./websocket-signaling.service');
                const signalingService = this.injector.get(WebSocketSignalingService);

                await webrtcMeshService.broadcastDataMessage({
                  type: 'full-attendance-sync-request',
                  eventId: eventId,
                  requesterId: signalingService.clientId
                }, 'system');
              }

            } catch (error) {
              console.warn('[EventService] WebRTC mesh refresh failed during event switch:', error);
              // Don't throw error, event switch should still succeed even if WebRTC fails
            }
          }
        }

        // Run integrity check to ensure count matches tickets
        await this.checkEventIntegrity(eventId);
      } else {
        console.error('[EventService] Failed to set current event - event not found:', eventId);
      }
    } catch (error) {
      console.error('[EventService] Error setting current event:', error);
    }
  }

  getTodaysEvents() {
    return this.events$.pipe(
      map((events: CounterEvent[]) => {
        const today = new Date().toISOString().split('T')[0];
        return events.filter((event: CounterEvent) =>
          event.date.toISOString().split('T')[0] === today
        );
      })
    );
  }

  getEventById(id: string): CounterEvent | undefined {
    return this.events().find(e => e.id === id);
  }

  async deleteEvent(eventId: string) {
    try {
      console.log(`[EventService] Deleting event ${eventId} and all related data`);

      // 1. Delete all tickets for this event from IndexedDB
      try {
        const deletedTickets = await this.attendanceService.deleteTicketsForEvent(eventId);
        console.log(`[EventService] ✅ Deleted ${deletedTickets} tickets for event ${eventId}`);
      } catch (error) {
        console.error('[EventService] ❌ Failed to delete tickets for event:', eventId, error);
        // Continue with event deletion even if ticket deletion fails
      }

      // 2. Remove from events array
      const events = this.events();
      const updatedEvents = events.filter(e => e.id !== eventId);
      await this.saveEventsToStorage(updatedEvents);

      // 3. Clean up ALL related storage keys for this event
      await this.cleanupEventStorage(eventId);

      // 4. If this was the current event, clear current event
      const currentEvent = this.getCurrentEvent();
      if (currentEvent && currentEvent.id === eventId) {
        this.currentEventSignal.set(null);
        await this.storage.remove(STORAGE_KEYS.CURRENT_EVENT);
        // Explicitly clear attendance service counts for the deleted event
        await this.attendanceService.clearCountsForEvent(eventId);
      }

      // 5. Notify server of event deletion (background, non-blocking)
      this.serverSyncService.recordEventDeletion(eventId).then(eventDeleted => {
        if (eventDeleted) {
          console.log(`[EventService] 🗑️ Event ${eventId} deleted from server (all devices deleted)`);
        } else {
          console.log(`[EventService] 📝 Event deletion recorded on server, waiting for other devices or 30-day retention`);
        }
      }).catch(err => {
        console.log(`[EventService] ⚠️ Failed to notify server of deletion, will retry later:`, err);
      });

      console.log(`[EventService] ✅ Event ${eventId} and all related data deleted successfully`);
      return true;
    } catch (error) {
      console.error('[Events] Failed to delete event:', error);
      return false;
    }
  }

  updateEventPassword(eventId: string, password: string): void {
    const events = this.getEvents();
    const eventIndex = events.findIndex(e => e.id === eventId);
    if (eventIndex < 0) return;

    const updatedEvents = [...events];
    const existing = updatedEvents[eventIndex];
    const updatedEvent = {
      ...existing,
      password
    } as CounterEvent;

    updatedEvents[eventIndex] = updatedEvent;
    this.eventsSignal.set(updatedEvents);
    void this.saveEventsToStorage(updatedEvents);

    const current = this.currentEventSignal();
    if (current && current.id === eventId) {
      this.currentEventSignal.set(updatedEvent);
    }
  }

  async removeEventLocal(eventId: string): Promise<boolean> {
    try {
      console.log(`[EventService] Removing event locally ${eventId}`);

      // 1. Delete all tickets for this event from IndexedDB
      try {
        await this.attendanceService.deleteTicketsForEvent(eventId);
      } catch (error) {
        console.error('[EventService] ❌ Failed to delete tickets for event:', eventId, error);
      }

      // 2. Remove from events array
      const events = this.events();
      const updatedEvents = events.filter(e => e.id !== eventId);
      await this.saveEventsToStorage(updatedEvents);

      // 3. Clean up ALL related storage keys for this event
      await this.cleanupEventStorage(eventId);

      // 4. If this was the current event, clear current event
      const currentEvent = this.getCurrentEvent();
      if (currentEvent && currentEvent.id === eventId) {
        this.currentEventSignal.set(null);
        await this.storage.remove(STORAGE_KEYS.CURRENT_EVENT);
        await this.attendanceService.clearCountsForEvent(eventId);
      }

      console.log(`[EventService] ✅ Event ${eventId} removed locally`);
      return true;
    } catch (error) {
      console.error('[Events] Failed to remove event locally:', error);
      return false;
    }
  }

  private async cleanupEventStorage(eventId: string) {
    try {
      // Storage keys to clean up for this event
      const keysToClean = [
        // Counter data
        `count_${eventId}`,
        // WebSocket event data (direct localStorage)
        `event_${eventId}`,
        `event_${eventId}_details`,
        // Token data (direct localStorage)  
        `token_${eventId}`,
        // Any other event-specific keys
        `attendance_${eventId}`,
        `settings_${eventId}`
      ];

      // Clean up StorageService keys (with prefix)
      for (const key of keysToClean) {
        await this.storage.remove(key);
      }

      // Clean up direct localStorage keys (without prefix)
      const directKeys = [
        `event_${eventId}`,
        `event_${eventId}_details`,
        `event_${eventId}_password`, // NEW: Add this key for cleanup
        `token_${eventId}`,
        `device_id_${eventId}`
      ];

      for (const key of directKeys) {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          console.log(`[EventService] Removed direct localStorage key: ${key}`);
        }
      }

      console.log(`[EventService] ✅ Cleaned up all storage for event ${eventId}`);
    } catch (error) {
      console.error(`[EventService] Error cleaning up storage for event ${eventId}:`, error);
    }
  }

  public refreshEvents(): void {
    const currentEvents = this.getEvents();
    this.eventsSignal.set([...currentEvents]);
  }

  public syncEvent(eventData: CounterEvent) {
    const events = this.getEvents();
    const existingIndex = events.findIndex(e => e.id === eventData.id);

    if (existingIndex >= 0) {
      events[existingIndex] = eventData;
    } else {
      events.push(eventData);
    }

    this.saveEventsToStorage(events);
    this.setCurrentEvent(eventData.id);
  }

  public addEvent(event: CounterEvent) {
    // Get current events array
    const currentEvents = this.getEvents();

    // Check if event already exists
    const existingIndex = currentEvents.findIndex(e => e.id === event.id);

    let updatedEvents: CounterEvent[];

    if (existingIndex >= 0) {
      // Update existing event
      updatedEvents = [...currentEvents];
      const existing = updatedEvents[existingIndex];
      updatedEvents[existingIndex] = {
        ...existing,
        ...event,
        // Preserve stored password when incoming payload is missing it
        password: event.password || existing.password
      };
    } else {
      // Add new event
      updatedEvents = [...currentEvents, event];
    }

    // Sort events by date
    const sortedEvents = updatedEvents.sort((a: CounterEvent, b: CounterEvent) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });

    // Notify subscribers
    this.eventsSignal.set(sortedEvents);

    // Save to storage
    void this.saveEventsToStorage(sortedEvents);
  }

  private saveEvents() {
    // Save events to storage using the async method
    void this.saveEventsToStorage(this.getEvents());
  }

  async checkEventExists(eventName: string, eventDate: Date): Promise<boolean> {
    const events = await this.getEvents();
    return events.some(e =>
      e.name === eventName &&
      new Date(e.date).getTime() === eventDate.getTime()
    );
  }

  /**
   * Mark an event as shared (for WebRTC/WebSocket multi-device events)
   */
  markEventAsShared(eventId: string, creatorDeviceId: string | null): void {
    const events = this.getEvents();
    const eventIndex = events.findIndex(e => e.id === eventId);

    if (eventIndex >= 0) {
      const updatedEvents = [...events];
      const existing = updatedEvents[eventIndex];

      // If creatorDeviceId is provided, set it. If null, clear it.
      const nextCreatorId = creatorDeviceId === null
        ? undefined
        : (creatorDeviceId || undefined);

      const updatedEvent = {
        ...existing,
        isSharedEvent: true,
        creatorDeviceId: nextCreatorId
      } as CounterEvent;

      updatedEvents[eventIndex] = updatedEvent;

      this.eventsSignal.set(updatedEvents);
      void this.saveEventsToStorage(updatedEvents);

      // Also update currentEvent signal if this is the active event so UI reflects shared status immediately
      const current = this.currentEventSignal();
      if (current && current.id === eventId) {
        this.currentEventSignal.set(updatedEvent);
      }
    }
  }

  parseLegacyTicketData(qrData: string): LegacyTicketData | null {
    try {
      const parts = qrData.split('*');
      if (parts.length !== 5) return null;

      return {
        zone: parts[0],
        venue: parts[1],
        date: parts[2],
        ticketNumber: parts[3],
        extraCode: parts[4]
      };
    } catch {
      return null;
    }
  }

  parseTicketQRData(qrData: string): TicketQRData | LegacyTicketData | null {
    // First try parsing as JSON (standard format)
    try {
      const data = JSON.parse(qrData);
      if (data.c && data.e && data.d && data.t) {
        // Check if this looks like a ticket ID (not a time)
        // Times typically have : separator (e.g., "11:16")
        const tValue = data.t.toString();
        if (tValue.includes(':') && tValue.length < 10) {
          // Likely a time, not a ticket ID - return null to trigger format mapper
          console.log('🔍 JSON has time in t field, not ticket ID - format mapper needed');
          return null;
        }
        return data as TicketQRData;
      }
    } catch { }

    // If JSON parsing fails, try legacy format
    const legacyData = this.parseLegacyTicketData(qrData);
    if (legacyData) {
      return legacyData;
    }

    return null;
  }

  isLegacyTicketData(data: any): data is LegacyTicketData {
    return 'venue' in data && 'zone' in data;
  }

  // Helper method to convert legacy date format to Date object
  private parseLegacyDate(dateStr: string): Date | null {
    try {
      const [day, month, year] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    } catch {
      return null;
    }
  }

  /**
   * Detect the structure of QR code content
   */
  detectQRStructure(qrData: string): any {
    // Try JSON parsing
    try {
      const parsed = JSON.parse(qrData);
      return { type: 'json', content: qrData, parsedData: parsed };
    } catch { }

    // Detect delimiter
    const delimiter = this.detectDelimiter(qrData);
    if (delimiter) {
      const parts = qrData.split(delimiter);
      return {
        type: 'delimited',
        delimiter,
        content: qrData,
        parsedData: parts
      };
    }

    return { type: 'unknown', content: qrData };
  }

  /**
   * Detect delimiter in delimited text
   */
  detectDelimiter(content: string): string | null {
    // Common delimiters ranked by frequency
    const delimiters = [
      { char: '*', name: 'asterisk' },
      { char: '|', name: 'pipe' },
      { char: ',', name: 'comma' },
      { char: ';', name: 'semicolon' },
      { char: '\t', name: 'tab' },
      { char: ':', name: 'colon' }
    ];

    for (const { char } of delimiters) {
      if (content.includes(char)) {
        const parts = content.split(char);
        if (parts.length >= 3 && parts.length <= 10) {
          // Reasonable number of fields
          return char;
        }
      }
    }

    return null;
  }

  /**
   * Save custom QR template for event
   */
  async saveCustomQRTemplate(eventId: string, template: any): Promise<void> {
    const events = await firstValueFrom(this.events$);
    const eventIndex = events.findIndex(e => e.id === eventId);

    if (eventIndex !== -1) {
      const normalized = this.normalizeTemplate(template as CustomQRTemplate, eventId);
      const existing = events[eventIndex].customQRTemplates || [];
      const existingIndex = existing.findIndex(t => t.id === normalized.id || (t.fingerprint && t.fingerprint === normalized.fingerprint));

      if (existingIndex >= 0) {
        existing[existingIndex] = {
          ...existing[existingIndex],
          ...normalized,
          updatedAt: new Date().toISOString()
        };
      } else {
        existing.push(normalized);
      }

      events[eventIndex].customQRTemplates = existing;
      // Keep legacy field in sync for backward compatibility.
      events[eventIndex].customQRTemplate = existing[0] || normalized;
      await this.storage.set(this.STORAGE_KEY, JSON.stringify(events));
      this.eventsSubject.next(events);
    }
  }

  /**
   * Get custom QR template for event
   */
  async getCustomQRTemplate(eventId: string): Promise<any | null> {
    const events = await firstValueFrom(this.events$);
    const event = events.find(e => e.id === eventId);
    if (!event) return null;
    const templates = event.customQRTemplates || [];
    if (templates.length > 0) {
      return templates[0];
    }
    return event.customQRTemplate || null;
  }

  async getCustomQRTemplates(eventId: string): Promise<CustomQRTemplate[]> {
    const events = await firstValueFrom(this.events$);
    const event = events.find(e => e.id === eventId);
    if (!event) return [];
    return event.customQRTemplates || (event.customQRTemplate ? [event.customQRTemplate] : []);
  }

  async setPrimaryCustomQRTemplate(eventId: string, templateId: string): Promise<void> {
    const events = await firstValueFrom(this.events$);
    const index = events.findIndex(e => e.id === eventId);
    if (index === -1) return;

    const templates = events[index].customQRTemplates || [];
    const target = templates.find(t => t.id === templateId);
    if (!target) return;

    const reordered = [target, ...templates.filter(t => t.id !== templateId)];
    events[index].customQRTemplates = reordered;
    events[index].customQRTemplate = reordered[0];
    await this.saveEventsToStorage(events);
  }

  async syncEventTemplatesFromServer(eventId: string): Promise<void> {
    try {
      const mappings = await this.serverSyncService.getEventQRMappings(eventId);
      if (!mappings.length) return;

      const events = [...this.getEvents()];
      const index = events.findIndex(e => e.id === eventId);
      if (index === -1) return;

      const existing = events[index].customQRTemplates || [];
      const merged = [...existing];
      for (const remoteTemplate of mappings) {
        const normalized = this.normalizeTemplate(remoteTemplate, eventId);
        const existingIndex = merged.findIndex(t => t.id === normalized.id || (t.fingerprint && t.fingerprint === normalized.fingerprint && JSON.stringify(t.fieldMappings) === JSON.stringify(normalized.fieldMappings)));
        if (existingIndex >= 0) {
          merged[existingIndex] = {
            ...merged[existingIndex],
            ...normalized
          };
        } else {
          merged.push(normalized);
        }
      }

      events[index].customQRTemplates = merged;
      events[index].customQRTemplate = merged[0];
      await this.saveEventsToStorage(events);

      if (this.currentEventSignal()?.id === eventId) {
        this.currentEventSignal.set(events[index]);
      }
    } catch (error) {
      console.warn('[EventService] Failed to sync event templates from server', error);
    }
  }

  private normalizeTemplate(template: CustomQRTemplate, eventId: string): CustomQRTemplate {
    return {
      ...template,
      eventId: template.eventId || eventId,
      id: template.id || `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      templateVersion: template.templateVersion || 1,
      source: template.source || 'event',
      successCount: template.successCount || 0,
      usageCount: template.usageCount || 0,
      isProven: !!template.isProven,
      status: template.status || 'active',
      confidence: typeof template.confidence === 'number' ? template.confidence : 0.5
    };
  }

  private normalizeEventTemplates(event: CounterEvent): CounterEvent {
    const templates: CustomQRTemplate[] = [];
    if (event.customQRTemplate) {
      templates.push(this.normalizeTemplate(event.customQRTemplate, event.id));
    }
    if (Array.isArray(event.customQRTemplates)) {
      event.customQRTemplates.forEach(template => {
        const normalized = this.normalizeTemplate(template, event.id);
        if (!templates.find(t => t.id === normalized.id)) {
          templates.push(normalized);
        }
      });
    }
    return {
      ...event,
      customQRTemplates: templates,
      customQRTemplate: templates[0]
    };
  }

  async findEventByNameAndDate(name: string, date: Date): Promise<CounterEvent | null> {
    const events = await firstValueFrom(this.events$);
    return events.find(event =>
      event.name === name &&
      event.date.getFullYear() === date.getFullYear() &&
      event.date.getMonth() === date.getMonth() &&
      event.date.getDate() === date.getDate() &&
      event.date.getHours() === date.getHours() &&
      event.date.getMinutes() === date.getMinutes()
    ) || null;
  }

  private buildQrEventSignature(params: {
    name: string;
    date: Date;
    ticketFormat?: string;
    venueCode?: string;
    zoneCode?: string;
  }): string {
    return [
      this.normalizeEventText(params.name),
      this.formatDateMinuteKey(params.date),
      this.normalizeEventText(params.ticketFormat || 'standard'),
      this.normalizeEventText(params.venueCode || ''),
      this.normalizeEventText(params.zoneCode || '')
    ].join('|');
  }

  private formatDateMinuteKey(date: Date): string {
    const safeDate = date instanceof Date ? date : new Date(date);
    const year = safeDate.getFullYear();
    const month = String(safeDate.getMonth() + 1).padStart(2, '0');
    const day = String(safeDate.getDate()).padStart(2, '0');
    const hours = String(safeDate.getHours()).padStart(2, '0');
    const minutes = String(safeDate.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}`;
  }

  private normalizeEventText(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }


  async refreshCurrentEvent() {
    const currentEvent = await this.getCurrentEventFromStorage();
    this.currentEventSignal.set(currentEvent);
  }

  private async getCurrentEventFromStorage(): Promise<CounterEvent | null> {
    const currentEventId = await this.storage.get(STORAGE_KEYS.CURRENT_EVENT);
    if (!currentEventId) return null;

    const events = await firstValueFrom(this.events$);
    return events.find(event => event.id === currentEventId) || null;
  }

  /**
   * Run comprehensive storage cleanup to remove orphaned data
   */
  async cleanupStorage(): Promise<void> {
    try {
      console.log('[EventService] Running comprehensive storage cleanup');

      // 1. Clean up storage service data
      await this.storage.cleanupAllStorage();

      // 2. Clean up orphaned tickets in IndexedDB
      const validEventIds = this.getEvents().map(event => event.id);
      try {
        const deletedOrphanedTickets = await this.attendanceService.cleanupOrphanedTickets(validEventIds);
        console.log(`[EventService] ✅ Cleaned up ${deletedOrphanedTickets} orphaned tickets`);
      } catch (error) {
        console.error('[EventService] ❌ Failed to cleanup orphaned tickets:', error);
      }

      console.log('[EventService] ✅ Comprehensive storage cleanup completed');
    } catch (error) {
      console.error('[EventService] ❌ Error during comprehensive cleanup:', error);
    }
  }

  /**
   * Check for potential event ID conflicts and handle count mismatches
   * This should be called when an event is loaded or app starts
   */
  async checkEventIntegrity(eventId: string): Promise<void> {
    try {
      const tickets = await this.attendanceService.getTicketsForEvent(eventId);
      const presentTickets = tickets.filter(ticket => ticket.isPresent);
      const actualCount = presentTickets.length;

      const storedCount = await this.attendanceService.getCount(eventId);

      console.log(`[EventService] Check integrity for ${eventId}: Stored=${storedCount}, ActualTickets=${actualCount}`);

      // Only auto-correct if actualCount is GREATER than storedCount
      // This prevents a new peer's zero count from overwriting the host's valid count
      if (actualCount > storedCount) {
        console.log(`[EventService] ⚠️ Count mismatch detected for event ${eventId}: Stored=${storedCount}, ActualTickets=${actualCount}. Auto-correcting to higher actual count.`);

        // Auto-correct the stored count to match tickets
        const event = this.getEventById(eventId);
        await this.attendanceService.switchEvent(eventId, event?.countMethod); // Ensure context is correct
        await this.storage.set(`count_${eventId}`, actualCount.toString());
        // Also update the in-memory signal of AttendanceService if it exists
        (this.attendanceService as any).countSignal?.set(actualCount);

        console.log(`[EventService] ✅ Auto-corrected count for event ${eventId} to ${actualCount}`);
      } else if (actualCount < storedCount) {
        console.warn(`[EventService] ⚠️ Possible count discrepancy for event ${eventId}: Stored=${storedCount}, ActualTickets=${actualCount}. Not auto-correcting down, consider manual audit.`);
        // Further action can be added here, e.g., flag for audit, notify user, etc.
      } else {
        console.log(`[EventService] ✅ Count for event ${eventId} is consistent: ${storedCount}`);
      }
    } catch (error: any) {
      console.error(`[EventService] Error checking event integrity for ${eventId}:`, error);
    }
  }

  async toggleFraudDetection(eventId: string, enabled: boolean): Promise<void> {
    try {
      const events = this.getEvents();
      const eventIndex = events.findIndex(e => e.id === eventId);

      if (eventIndex !== -1) {
        events[eventIndex].enableFraudDetection = enabled;

        // Update signals
        this.eventsSignal.set([...events]);

        // Update current event if it's the one being modified
        const currentEvent = this.currentEventSignal();
        if (currentEvent && currentEvent.id === eventId) {
          this.currentEventSignal.set({ ...currentEvent, enableFraudDetection: enabled });
        }

        // Save to storage
        await this.saveEventsToStorage(events);

        console.log(`[EventService] ✅ Fraud detection ${enabled ? 'enabled' : 'disabled'} for event:`, eventId);
      } else {
        console.warn(`[EventService] ⚠️ Event not found for fraud detection toggle:`, eventId);
      }
    } catch (error) {
      console.error(`[EventService] ❌ Error toggling fraud detection for event ${eventId}:`, error);
      throw error;
    }
  }

  /**
   * Manually refresh WebRTC mesh connections for the current event
   * Useful when user suspects their data might be stale
   */
  async refreshWebRTCMeshConnection(): Promise<boolean> {
    try {
      const currentEvent = this.currentEventSignal();
      if (!currentEvent) {
        console.log('[EventService] No current event for WebRTC mesh refresh');
        return false;
      }

      console.log('[EventService] Manually refreshing WebRTC mesh connections for event:', currentEvent.name);

      // Import WebRTC service dynamically to avoid circular dependencies
      const { WebrtcMeshService } = await import('./webrtc-mesh.service');
      const webrtcMeshService = this.injector.get(WebrtcMeshService);

      if (webrtcMeshService) {
        // Clean up existing mesh connections
        console.log('[EventService] Cleaning up existing WebRTC mesh');
        webrtcMeshService.cleanupMesh();

        // Small delay to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 200));

        // Try to reconnect
        console.log('[EventService] Attempting to reconnect to WebRTC mesh');
        const success = await webrtcMeshService.reconnectToEvent(currentEvent.id);

        if (success) {
          console.log('[EventService] WebRTC mesh refresh successful');
          return true;
        } else {
          console.warn('[EventService] WebRTC mesh refresh failed');
          return false;
        }
      } else {
        console.warn('[EventService] WebRTC mesh service not available');
        return false;
      }
    } catch (error) {
      console.error('[EventService] Error refreshing WebRTC mesh connection:', error);
      return false;
    }
  }
}